import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import axiosInstance, { checkTokenValidity, getUserDetails } from '../../components/content/axiosConfig';
import { fetchPondsForCurrentUser } from '../../utils/pondListForUser';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import LengthUnitDropDownSelection from '../../components/custom/LengthUnitDropDownSelection';
import WeightUnitDropDownSelection from '../../components/custom/WeightUnitDropDown';
import FishTypesSelection from '../../components/custom/FishTypesSelection';
import SamplingEffortFields from '../../components/custom/SamplingEffortFields';
import ClockTimeInput from '../../components/custom/ClockTimeInput';
import { requiresEffortUnits, isAnglingSamplingMethod, SAMPLING_METHOD_OPTIONS, isValidClockTimeInput } from '../../utils/samplingCpueUtils';
import { displayMethodLabel } from '../../utils/samplingDisplayLabels';

const WQ_FIELD_STYLE = {
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  height: '50px',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  backgroundColor: '#fafafa',
};

const wqFocusGreen = (e) => {
  e.target.style.borderColor = '#32a932';
  e.target.style.backgroundColor = '#fff';
  e.target.style.boxShadow = '0 0 0 3px rgba(50, 169, 50, 0.1)';
};
const wqBlurDefault = (e) => {
  e.target.style.borderColor = '#e0e0e0';
  e.target.style.backgroundColor = '#fafafa';
  e.target.style.boxShadow = 'none';
};

const WQ_EXTRA_FIELD_DEFS = [
  { id: 'BOD5', label: 'BOD5, mg/L' },
  { id: 'COD', label: 'COD, mg/L' },
  { id: 'Alkalinity', label: 'Alkalinity, mg/L CaCO3' },
  { id: 'NitriteN', label: 'Nitrite-N, mg/L NO2-N' },
  { id: 'TotalHardness', label: 'Total Hardness, mg/L CaCO3' },
  { id: 'PhosphateIon', label: 'Phosphate Ion, mg/L PO4' },
  { id: 'Phosphorous', label: 'Phosphorus, mg/L P' },
  { id: 'TotalAmmoniaNitrogen', label: 'Total Ammonia Nitrogen, mg/L NH3-N' },
];

const optionalWQFloat = (v) => {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const CreatePondDetail = () => {
  const history = useHistory();
  const { id } = useParams();
  const sessionFisherID = getUserDetails()?.FisherID;
  const [ponds, setPonds] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const [selectedFishTypes, setSelectedFishTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [selectedWeightUnit, setSelectedWeightUnit] = useState('');
  const [selectedLengthUnit, setSelectedLengthUnit] = useState('');

  // Helper functions for date formatting
  const formatDate = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return '';
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatLocalDateTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initial form state
  const initialFormState = {
    PondID: id || '0',
    Name: formatDate(new Date()),
    Date: new Date(),
    TimeFishingStarted: '',
    TimeFishingStopped: '',
    SamplingMethod: '',
    AirTemperature: '',
    SkyClimate: '',
    WindSpeed: '',
    WindDirection: '',
    MoonPhase: '',
    BaroMetricPressure: '',
    WaterTemperature: '',
    WaterClarity: '',
    PH: '',
    DO: '',
    BOD5: '',
    COD: '',
    Alkalinity: '',
    NitriteN: '',
    TotalHardness: '',
    PhosphateIon: '',
    Phosphorous: '',
    TotalAmmoniaNitrogen: '',
    NoFishes: 0,
    NoFishTypes: 0,
    FisherID: sessionFisherID ?? '',
    Weight_Unit: '',
    Length_Unit: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [effortCount, setEffortCount] = useState('');
  const [effortUnits, setEffortUnits] = useState([]);
  const [anglerCount, setAnglerCount] = useState("");
  const [fisherStaffOptions, setFisherStaffOptions] = useState([]);
  const [selectedSessionAnglers, setSelectedSessionAnglers] = useState([]);

  // Fetch fish types
  const fetchFishTypes = useCallback(async (pondId) => {
    if (!pondId || pondId === '0') return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondId}`, {
        params: { search: '' },
      });
      setFishTypes(response.data || []);
      setSelectedFishTypes([]);
    } catch (error) {
      handleApiError(error, 'Failed to fetch fish types');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch ponds data
  const fetchPonds = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetchedPonds = await fetchPondsForCurrentUser(getUserDetails());
      setPonds(fetchedPonds);

      if (id) {
        const selectedPond = fetchedPonds.find((pond) => pond.id === Number(id));
        if (selectedPond) {
          setFormData((prev) => ({
            ...prev,
            PondID: id,
            FisherID: selectedPond.FisherID ?? sessionFisherID ?? prev.FisherID,
            Name: `${selectedPond.Name}-${formatDate(new Date())}`,
          }));
          fetchFishTypes(id);
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to fetch ponds');
    } finally {
      setIsLoading(false);
    }
  }, [sessionFisherID, id, fetchFishTypes]);

  // Handle API errors
  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.reload();
      showErrorToast('Session expired. Please log in again.');
    } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error('API Error:', error);
    }
  };

  // Handle pond selection change
  const handlePondChange = (e) => {
    const { value } = e.target;
    const selectedPond = ponds.find((item) => item.id === Number(value));

    setFormErrors((prev) => ({ ...prev, PondID: '' }));
    setFormData((prev) => ({
      ...prev,
      PondID: value,
      FisherID: selectedPond?.FisherID ?? sessionFisherID ?? prev.FisherID,
      Name: selectedPond ? `${selectedPond.Name}-${formatDate(prev.Date || new Date())}` : formatDate(prev.Date || new Date()),
    }));

    fetchFishTypes(value);
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormErrors((prev) => ({ ...prev, [id]: '' }));
    if (id === 'SamplingMethod') {
      setEffortCount('');
      setEffortUnits([]);
      if (!isAnglingSamplingMethod(value)) {
        setAnglerCount('');
        setSelectedSessionAnglers([]);
      }
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const fetchFisherTeamStaff = useCallback(async (scopedFisherID) => {
    const fid = scopedFisherID ?? formData.FisherID ?? sessionFisherID;
    if (!fid) {
      setFisherStaffOptions([]);
      return;
    }
    try {
      const res = await axiosInstance.get('/api/PondDetail/FisherTeamStaff', {
        params: { FisherID: fid },
      });
      const options = (res.data?.staff || []).map((s) => ({
        value: s.id,
        label: s.fullName,
      }));
      setFisherStaffOptions(options);
    } catch {
      setFisherStaffOptions([]);
    }
  }, [formData.FisherID, sessionFisherID]);

  const handleSessionAnglersChange = (selected) => {
    const next = selected || [];
    setSelectedSessionAnglers(next);
    setAnglerCount(next.length > 0 ? String(next.length) : '');
    setFormErrors((prev) => ({ ...prev, SessionAnglers: '' }));
  };

  const saveSessionAnglers = async (pondDetailId, selected) => {
    if (!selected?.length) return;
    await axiosInstance.put(`/api/SamplingSessionAnglers/${pondDetailId}`, {
      staffIds: selected.map((s) => s.value),
    });
  };

  const saveEffortUnits = async (pondDetailId, samplingMethod, units) => {
    if (!requiresEffortUnits(samplingMethod) || !units?.length) return;
    await axiosInstance.put(`/api/SamplingEffort/${pondDetailId}`, {
      samplingMethod,
      units,
    });
  };

  // Handle date changes — ignore empty/invalid picker events (they used to snap to now).
  const handleDateChange = (e) => {
    const newDateTime = e.target.value;
    if (!newDateTime) return;
    const newDate = new Date(newDateTime);
    if (Number.isNaN(newDate.getTime())) return;

    const formattedDate = formatDate(newDate);
    const selectedPond = ponds.find((item) => item.id === Number(formData.PondID));

    setFormData((prev) => ({
      ...prev,
      Date: newDate,
      Name: selectedPond ? `${selectedPond.Name}-${formattedDate}` : formattedDate,
    }));
  };

  // Handle unit selection changes
  const handleWeightUnitChange = (selectedOption) => {
    const weightUnitValue = selectedOption?.value || '';
    setSelectedWeightUnit(weightUnitValue);
    setFormData((prev) => ({ ...prev, Weight_Unit: weightUnitValue }));
    setFormErrors((prev) => ({ ...prev, Weight_Unit: '' }));
  };

  const handleLengthUnitChange = (selectedOption) => {
    const lengthUnitValue = selectedOption?.value || '';
    setSelectedLengthUnit(lengthUnitValue);
    setFormData((prev) => ({ ...prev, Length_Unit: lengthUnitValue }));
    setFormErrors((prev) => ({ ...prev, Length_Unit: '' }));
  };

  // Handle fish types change
  const handleFishTypesChange = (selectedOptions) => {
    setSelectedFishTypes(selectedOptions || []);
    setFormErrors((prev) => ({ ...prev, FishTypes: '' }));
    setFormData((prev) => ({
      ...prev,
      NoFishTypes: selectedOptions ? selectedOptions.length : 0,
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    const requiredFields = [
      { field: 'PondID', message: 'Please select a pond', check: (val) => val && val !== '0' },
      { field: 'Name', message: 'Name is required', check: (val) => val?.trim() },
      { field: 'TimeFishingStarted', message: 'Time fishing started is required (h:mm AM/PM)', check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: 'TimeFishingStopped', message: 'Time fishing stopped is required (h:mm AM/PM)', check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: 'SamplingMethod', message: 'Sampling method is required', check: (val) => val?.trim() },
      { field: 'Weight_Unit', message: 'Weight unit is required', check: (val) => val },
      { field: 'Length_Unit', message: 'Length unit is required', check: (val) => val },
      { field: 'FishTypes', message: 'At least one fish type is required', check: () => selectedFishTypes.length > 0 },
    ];

    requiredFields.forEach(({ field, message, check }) => {
      if (!check(formData[field])) {
        errors[field] = message;
        isValid = false;
      }
    });

    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      if (!selectedSessionAnglers.length) {
        errors.SessionAnglers = 'Select at least one angler for this fishing session';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        FisherID: formData.FisherID || sessionFisherID,
        Date: formData.Date?.toISOString() || new Date().toISOString(),
        NumberOfAnglers: isAnglingSamplingMethod(formData.SamplingMethod)
          ? parseInt(anglerCount, 10)
          : null,
        FertilizationDate: null,
        FertilizationType: '',
        BOD5: optionalWQFloat(formData.BOD5),
        COD: optionalWQFloat(formData.COD),
        Alkalinity: optionalWQFloat(formData.Alkalinity),
        NitriteN: optionalWQFloat(formData.NitriteN),
        TotalHardness: optionalWQFloat(formData.TotalHardness),
        PhosphateIon: optionalWQFloat(formData.PhosphateIon),
        Phosphorous: optionalWQFloat(formData.Phosphorous),
        TotalAmmoniaNitrogen: optionalWQFloat(formData.TotalAmmoniaNitrogen),
      };

      const response = await axiosInstance.post('/api/PondDetail/AddPondDetail', payload);
      const pondDetailId = response.data.pondDetail.id;

      await saveEffortUnits(pondDetailId, formData.SamplingMethod, effortUnits);
      if (isAnglingSamplingMethod(formData.SamplingMethod)) {
        await saveSessionAnglers(pondDetailId, selectedSessionAnglers);
      }

      let existingFishTypes = [];
      try {
        const fishResponse = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${pondDetailId}`);
        existingFishTypes = fishResponse.data || [];
      } catch (error) {
        console.warn('No existing fish types found or error fetching:', error);
      }

      const selectedFishTypeIds = selectedFishTypes.map((option) => option.value);
      const existingFishTypeIds = existingFishTypes.map((fish) => fish.FishTypeID);

      const fishTypesToAdd = selectedFishTypeIds.filter((id) => !existingFishTypeIds.includes(id));
      const fishTypesToDelete = existingFishTypes.filter((fish) => !selectedFishTypeIds.includes(fish.FishTypeID));

      for (const fishTypeId of fishTypesToAdd) {
        await axiosInstance.post(`/api/SamplingFishes/AddFish`, {
          PondDID: pondDetailId,
          FishTypeID: fishTypeId,
          FisherID: formData.FisherID || sessionFisherID,
          NumberOfFishes: 0,
          AverageLength: 0,
          StandardDeviationLength: 0,
          AverageWeight: 0,
          StandardDeviationWeight: 0,
          AverageWr: 0,
          StandardDeviationWr: 0,
          NumberOfFishesReleased: 0,
          NumberOfFishesCulled: 0,
        });
      }

      for (const fish of fishTypesToDelete) {
        await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${fish.PondFishesID}`);
      }

      showSuccessToast('Pond detail and fish types saved successfully');
      history.push(`/Updatepd/${pondDetailId}`);
    } catch (error) {
      handleApiError(error, 'Failed to save pond detail or fish types');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    checkTokenValidity();
    fetchPonds();
  }, [fetchPonds]);

  useEffect(() => {
    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      fetchFisherTeamStaff(formData.FisherID);
    } else {
      setFisherStaffOptions([]);
      setSelectedSessionAnglers([]);
    }
  }, [formData.SamplingMethod, formData.FisherID, fetchFisherTeamStaff]);

  useEffect(() => {
    if (!isAnglingSamplingMethod(formData.SamplingMethod)) return;
    if (selectedSessionAnglers.length > 0) return;
    const user = getUserDetails();
    if (!user?.id || !fisherStaffOptions.length) return;
    const me = fisherStaffOptions.find((o) => String(o.value) === String(user.id));
    if (me) {
      handleSessionAnglersChange([me]);
    }
  }, [formData.SamplingMethod, fisherStaffOptions, selectedSessionAnglers.length]);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-workflow-page py-3">
      <form onSubmit={handleSubmit}>
        <div className="container-fluid px-3 px-md-4">
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0">
              <ul className="breadcrumb mb-2" style={{ fontSize: '0.875rem' }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: '#6c757d', textDecoration: 'none' }}>Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/fishingData" style={{ color: '#6c757d', textDecoration: 'none' }}>Fishing Data</Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: '#495057' }}>Create Fishing / Sampling Data</li>
              </ul>
              <h1 className="mb-0" style={{ fontSize: '1.75rem', fontWeight: '600', color: '#2d353c' }}>
                Create Fishing / Sampling Data {formData.Name && `(${formData.Name})`}
              </h1>
            </div>
            <button 
              type="submit" 
              className="btn btn-success px-4 py-2 rounded-pill shadow-sm"
              style={{ 
                fontSize: '0.9rem',
                fontWeight: '500',
                backgroundColor: '#28a745',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              disabled={isLoading}
              onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ) : (
                <i className="fa fa-plus me-2"></i>
              )}
              Submit
            </button>
          </div>

          <div className="row g-3">
            {/* Basic Information Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2" style={{ borderBottom: '2px solid #e9ecef' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1rem', fontWeight: '600' }}>
                    <i className="fa fa-info-circle me-2" style={{ color: "var(--bs-primary)", fontSize: '0.9rem' }}></i>
                    Basic Information
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <select
                          className={`form-control ${formErrors.PondID ? 'is-invalid' : ''}`}
                          id="PondID"
                          name="PondID"
                          disabled={!!id}
                          value={formData.PondID}
                          onChange={handlePondChange}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="0">Select Pond</option>
                          {ponds.map((pond) => (
                            <option key={pond.id} value={pond.id}>
                              {pond.Name}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="PondID" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Pond <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.PondID && <div className="invalid-feedback">{formErrors.PondID}</div>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <input
                          type="datetime-local"
                          className={`form-control ${formErrors.Date ? 'is-invalid' : ''}`}
                          id="Date"
                          value={formData.Date ? formatLocalDateTime(formData.Date) : ''}
                          onChange={handleDateChange}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="Date" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Sampling/Fishing Date & Time <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.Date && <div className="invalid-feedback">{formErrors.Date}</div>}
                      </div>
                    </div>

                    <div className="col-12">
                      <FishTypesSelection
                        fishTypes={fishTypes}
                        selectedFishTypes={selectedFishTypes}
                        onChange={handleFishTypesChange}
                        error={formErrors.FishTypes}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sampling Details Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2" style={{ borderBottom: '2px solid #e9ecef' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1rem', fontWeight: '600' }}>
                    <i className="fa fa-clock me-2" style={{ color: '#49b6d6', fontSize: '0.9rem' }}></i>
                    Sampling Details
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <div className="form-floating mb-2">
                        <ClockTimeInput
                          className={`form-control ${formErrors.TimeFishingStarted ? 'is-invalid' : ''}`}
                          id="TimeFishingStarted"
                          value={formData.TimeFishingStarted}
                          onChange={handleChange}
                          placeholder="1200 AM"
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="TimeFishingStarted" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Time Started (h:mm AM/PM) <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.TimeFishingStarted && (
                          <div className="invalid-feedback">{formErrors.TimeFishingStarted}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating mb-2">
                        <ClockTimeInput
                          className={`form-control ${formErrors.TimeFishingStopped ? 'is-invalid' : ''}`}
                          id="TimeFishingStopped"
                          value={formData.TimeFishingStopped}
                          onChange={handleChange}
                          placeholder="1214 PM"
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="TimeFishingStopped" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Time Stopped (h:mm AM/PM) <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.TimeFishingStopped && (
                          <div className="invalid-feedback">{formErrors.TimeFishingStopped}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <select
                          className={`form-control ${formErrors.SamplingMethod ? 'is-invalid' : ''}`}
                          id="SamplingMethod"
                          value={formData.SamplingMethod}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="">Select Sampling Method</option>
                          {SAMPLING_METHOD_OPTIONS.map((m) => (
                            <option key={m} value={m}>{displayMethodLabel(m)}</option>
                          ))}
                        </select>
                        <label htmlFor="SamplingMethod" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Sampling Method <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.SamplingMethod && (
                          <div className="invalid-feedback">{formErrors.SamplingMethod}</div>
                        )}
                      </div>
                    </div>

                    <SamplingEffortFields
                      samplingMethod={formData.SamplingMethod}
                      effortCount={effortCount}
                      onEffortCountChange={setEffortCount}
                      effortUnits={effortUnits}
                      onEffortUnitsChange={setEffortUnits}
                      anglerCount={anglerCount}
                      fisherStaffOptions={fisherStaffOptions}
                      selectedSessionAnglers={selectedSessionAnglers}
                      onSessionAnglersChange={handleSessionAnglersChange}
                      sessionAnglersError={formErrors.SessionAnglers}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Weather Conditions Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3" style={{ borderBottom: '2px solid #f0f0f0', background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 100%)' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="fa fa-cloud-sun me-2" style={{ color: '#ff9800', fontSize: '1rem' }}></i>
                    Weather Conditions
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.AirTemperature ? 'is-invalid' : ''}`}
                          id="AirTemperature"
                          value={formData.AirTemperature}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="AirTemperature" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Air Temperature (°F)
                        </label>
                        {formErrors.AirTemperature && (
                          <div className="invalid-feedback">{formErrors.AirTemperature}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.SkyClimate ? 'is-invalid' : ''}`}
                          id="SkyClimate"
                          value={formData.SkyClimate}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Sky</option>
                          <option value="Sunny">Sunny</option>
                          <option value="Cloudy">Cloudy</option>
                          <option value="Partly Cloudy">Partly Cloudy</option>
                          <option value="Rain">Rain</option>
                          <option value="Snow">Snow</option>
                        </select>
                        <label htmlFor="SkyClimate" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Sky
                        </label>
                        {formErrors.SkyClimate && (
                          <div className="invalid-feedback">{formErrors.SkyClimate}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.WindSpeed ? 'is-invalid' : ''}`}
                          id="WindSpeed"
                          value={formData.WindSpeed}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="WindSpeed" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Wind Speed (mph)
                        </label>
                        {formErrors.WindSpeed && (
                          <div className="invalid-feedback">{formErrors.WindSpeed}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.WindDirection ? 'is-invalid' : ''}`}
                          id="WindDirection"
                          value={formData.WindDirection}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Wind Direction</option>
                          <option value="North">North</option>
                          <option value="Northeast">Northeast</option>
                          <option value="East">East</option>
                          <option value="Southeast">Southeast</option>
                          <option value="South">South</option>
                          <option value="Southwest">Southwest</option>
                          <option value="West">West</option>
                          <option value="Northwest">Northwest</option>
                        </select>
                        <label htmlFor="WindDirection" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Wind Direction
                        </label>
                        {formErrors.WindDirection && (
                          <div className="invalid-feedback">{formErrors.WindDirection}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.MoonPhase ? 'is-invalid' : ''}`}
                          id="MoonPhase"
                          value={formData.MoonPhase}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Moon Phase</option>
                          <option value="New">New</option>
                          <option value="First Qtr">First Qtr</option>
                          <option value="Full">Full</option>
                          <option value="Last Qtr">Last Qtr</option>
                        </select>
                        <label htmlFor="MoonPhase" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Moon Phase
                        </label>
                        {formErrors.MoonPhase && (
                          <div className="invalid-feedback">{formErrors.MoonPhase}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.BaroMetricPressure ? 'is-invalid' : ''}`}
                          id="BaroMetricPressure"
                          value={formData.BaroMetricPressure}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="BaroMetricPressure" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Barometric Pressure (mmHg)
                        </label>
                        {formErrors.BaroMetricPressure && (
                          <div className="invalid-feedback">{formErrors.BaroMetricPressure}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Measuring units */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3" style={{ borderBottom: '2px solid #f0f0f0', background: 'linear-gradient(135deg, #eef4fb 0%, #ffffff 100%)' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="fa fa-ruler-combined me-2" style={{ color: "var(--bs-primary)", fontSize: '1rem' }}></i>
                    Measuring Units
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <WeightUnitDropDownSelection
                          selectedValue={selectedWeightUnit}
                          onChange={handleWeightUnitChange}
                          error={formErrors.Weight_Unit}
                        />
                        {formErrors.Weight_Unit && (
                          <div className="invalid-feedback d-block">{formErrors.Weight_Unit}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <LengthUnitDropDownSelection
                          selectedValue={selectedLengthUnit}
                          onChange={handleLengthUnitChange}
                          error={formErrors.Length_Unit}
                        />
                        {formErrors.Length_Unit && (
                          <div className="invalid-feedback d-block">{formErrors.Length_Unit}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Water Quality Panel — 3-column grid */}
            <div className="col-12">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3" style={{ borderBottom: '2px solid #f0f0f0', background: 'linear-gradient(135deg, #f0f9f4 0%, #ffffff 100%)' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="fa fa-tint me-2" style={{ color: '#32a932', fontSize: '1rem' }}></i>
                    Water Quality
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.WaterTemperature ? 'is-invalid' : ''}`}
                          id="WaterTemperature"
                          value={formData.WaterTemperature}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="WaterTemperature" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Water Temperature, F
                        </label>
                        {formErrors.WaterTemperature && (
                          <div className="invalid-feedback">{formErrors.WaterTemperature}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          id="WaterClarity"
                          value={formData.WaterClarity}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="WaterClarity" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Water Clarity, ft.
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.PH ? 'is-invalid' : ''}`}
                          id="PH"
                          value={formData.PH}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="PH" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          pH (1-14)
                        </label>
                        {formErrors.PH && <div className="invalid-feedback">{formErrors.PH}</div>}
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          id="DO"
                          value={formData.DO}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="DO" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Dissolved Oxygen, mg/L
                        </label>
                      </div>
                    </div>
                    {WQ_EXTRA_FIELD_DEFS.slice(0, 2).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                    {WQ_EXTRA_FIELD_DEFS.slice(2, 5).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                    {WQ_EXTRA_FIELD_DEFS.slice(5, 8).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePondDetail;
